// ---------- l'accueil : le chemin d'un chapitre ----------
// La maquette « J » : le mot du jour, le nom du chapitre entre deux flèches (‹ › pour revoir un
// chapitre fini ou regarder celui qui vient), le chemin du chapitre sur son décor, et une carte en
// bas pour la partie touchée. Le fond est celui du thème du chapitre regardé.

import { mapGeometry, MAP_W } from './path';
import {
  currentOf,
  doneCount,
  frontier,
  isOpen,
  passesLeft,
  stateOf,
  toUnlock,
  type NodeState,
} from './quest';
import { lastChapter, rememberChapter, skip, starsOf, streak } from './store';
import { dismissInvite, inviteBanner, shouldInvite } from './install';
import { $, applyTheme, decorSvg, esc, ICON, safeColor, STAR, topBar } from './ui';
import { t } from './i18n';
import type { Content, PuzzleSummary } from '../shared/types';

export interface HomeHooks {
  play(chapterIndex: number, puzzle: PuzzleSummary): void;
  daily(): void;
  settings(): void;
}

let content: Content;
let hooks: HomeHooks;
let view = 0;
let sel: string | null = null;
let wired = false;

const ROT = [-4, 3, -2, 4, -3, 2, -4, 3, -2, 0];

/** Après ce délai sans un toucher, l'accueil se met au repos (cf. `.home.calm`, style.css). */
const CALM_MS = 12_000;
let calmTimer = 0;
function wake(): void {
  $('home').classList.remove('calm');
  clearTimeout(calmTimer);
  calmTimer = window.setTimeout(() => $('home').classList.add('calm'), CALM_MS);
}

export function initHome(c: Content, h: HomeHooks): void {
  content = c;
  hooks = h;
  const remembered = c.chapters.findIndex((ch) => ch.id === lastChapter());
  view = remembered >= 0 && isOpen(c, remembered) ? remembered : frontier(c);
  // Une fois : `boot()` peut repasser ici (Réessayer après une panne réseau), et deux écouteurs
  // feraient compter chaque toucher deux fois — deux « Passer » d'un coup.
  if (wired) return;
  wired = true;
  $('home').addEventListener('click', onClick);
  $('home').addEventListener('pointerdown', wake);
}

/** Redessine l'accueil, sur le chapitre `index` si on le donne (retour de partie). */
export function renderHome(index?: number): void {
  wake();
  // Au retour d'une partie, la carte montre la SUIVANTE, pas celle qu'on vient de finir.
  if (index !== undefined) {
    view = index;
    sel = null;
  }
  const ch = content.chapters[view];
  rememberChapter(ch.id);
  const theme = content.themes.find((x) => x.id === ch.themeId);
  applyTheme(theme);
  const open = isOpen(content, view);
  const cur = open ? currentOf(ch) : undefined;
  if (!sel || !ch.puzzles.some((p) => p.id === sel)) sel = (cur ?? ch.puzzles[0])?.id ?? null;
  const stars = ch.puzzles.reduce((a, p) => a + starsOf(p.id), 0);
  const done = doneCount(ch);
  const geo = mapGeometry(ch.puzzles.length);
  const walkTo = cur
    ? geo.along[ch.puzzles.indexOf(cur)]
    : open && done === ch.puzzles.length
      ? 1000
      : 0;
  const frontierCh = content.chapters[frontier(content)];
  const states = ch.puzzles.map((p) => stateOf(content, view, p));
  const waiting = states.filter((s) => s === 'skipped').length;
  const left = toUnlock(content, view);
  const progress = !open
    ? t('chap.locked')
    : done === ch.puzzles.length
      ? t('chap.done', { stars, max: ch.puzzles.length * 3 })
      : [
          t('chap.progress', { done, total: ch.puzzles.length }),
          waiting ? t('chap.waiting', { n: waiting }) : '',
          left ? t('chap.toUnlock', { n: left }) : t('chap.unlocked'),
        ]
          .filter(Boolean)
          .join(' · ');
  const s = streak();
  // Le décor est dessiné pour un cadre de 390 × 400 (trois rangées) ; un chapitre plus long le
  // répète vers le bas plutôt que de l'étirer.
  const decor = decorSvg(theme);
  const decors = decor
    ? Array.from(
        { length: Math.ceil(geo.height / 400) },
        (_, k) =>
          `<div class="decor" style="top:${(k * 400 * 100) / geo.height}%;height:${(400 * 100) / geo.height}%">${decor}</div>`,
      ).join('')
    : '';
  $('home').innerHTML = `
    ${topBar(t('bar.chapter', { n: frontierCh.position, title: frontierCh.title }), `<span class="pill" aria-label="${t('bar.stars', { n: stars })}">${STAR}${stars}</span><button class="icon-btn" type="button" data-act="settings" aria-label="${t('settings.open')}">${ICON.gear}</button>`)}
    ${shouldInvite() && content.chapters.some((c) => doneCount(c) > 0) ? inviteBanner() : ''}
    <button class="daily" type="button" data-act="daily">
      <span class="daily-ic">${ICON.calendar}</span>
      <span class="daily-txt"><b>${t('daily.title')}</b><span>${t('daily.sub')}</span></span>
      ${s ? `<span class="daily-streak" aria-label="${t('daily.streakAria', { n: s })}">${ICON.flame}${t('daily.streak', { n: s })}</span>` : ''}
    </button>
    <div class="chap">
      <div class="chap-row">
        <button class="chap-arrow" type="button" data-act="prev" aria-label="${t('chap.prev')}" ${view === 0 ? 'disabled' : ''}>${ICON.prev}</button>
        <div class="chap-name"><small>${t('chap.num', { n: ch.position })}</small><b>${esc(ch.title)}</b></div>
        <button class="chap-arrow" type="button" data-act="next" aria-label="${t('chap.next')}" ${view >= content.chapters.length - 1 ? 'disabled' : ''}>${ICON.next}</button>
      </div>
      <div class="chap-bar"><i><b style="width:${(done / Math.max(1, ch.puzzles.length)) * 100}%"></b></i><span>${progress}</span></div>
    </div>
    <div class="map-box">
      <div class="map" style="aspect-ratio:${MAP_W} / ${geo.height}">
        ${decors}
        <svg class="road" viewBox="0 0 ${MAP_W} ${geo.height}" aria-hidden="true">
          <path d="${geo.road}" class="road-edge"/>
          <path d="${geo.road}" class="road-sand" style="stroke:${safeColor(theme?.road, '#f3e2bf')}"/>
          <path d="${geo.road}" class="road-walk" pathLength="1000" style="stroke-dasharray:${walkTo} 1000"/>
          <path d="${geo.road}" class="road-dash" style="stroke:${safeColor(theme?.dash, 'rgba(255,255,255,.8)')}"/>
        </svg>
        ${ch.puzzles.map((p, i) => node(p, i, states[i], geo.nodes[i], geo.height, i === ch.puzzles.length - 1)).join('')}
      </div>
    </div>
    ${sheet(
      ch.puzzles.find((p) => p.id === sel),
      states,
      open,
    )}`;
}

function node(
  p: PuzzleSummary,
  i: number,
  st: NodeState,
  at: { x: number; y: number },
  h: number,
  last: boolean,
): string {
  const n = i + 1;
  const aria = st === 'done' ? t('node.done', { n, stars: starsOf(p.id) }) : t(`node.${st}`, { n });
  const stars = starsOf(p.id);
  return `<div class="node ${st}${last ? ' boss' : ''}${p.id === sel ? ' sel' : ''}" style="left:${(at.x / MAP_W) * 100}%;top:${(at.y / h) * 100}%;--rot:${ROT[i % ROT.length]}deg">
    <button type="button" data-act="pick" data-id="${p.id}" aria-label="${aria}">${st === 'locked' ? ICON.lock : n}</button>
    ${last ? `<span class="crown">${ICON.crown}</span>` : ''}
    ${st === 'skipped' ? `<span class="flag">${ICON.flag}</span>` : ''}
    ${st === 'done' ? `<span class="stars">${[1, 2, 3].map((k) => `<i class="${k <= stars ? 'on' : ''}">${STAR}</i>`).join('')}</span>` : ''}
    ${st === 'current' ? `<span class="here">${t('node.here')}</span>` : ''}
  </div>`;
}

function sheet(p: PuzzleSummary | undefined, states: NodeState[], open: boolean): string {
  const ch = content.chapters[view];
  if (!p) return '';
  const i = ch.puzzles.indexOf(p);
  const st = states[i];
  const n = i + 1;
  const title = i === ch.puzzles.length - 1 ? t('sheet.last', { n }) : t('sheet.part', { n });
  let meta: string;
  if (!open) {
    const prev = content.chapters[view - 1];
    meta = t('sheet.chapterLocked', {
      need: Math.min(content.rules.unlockAt, prev.puzzles.length),
      left: toUnlock(content, view - 1),
    });
  } else if (st === 'done') meta = t('sheet.done', { stars: starsOf(p.id), rows: p.length });
  else if (st === 'skipped') meta = t('sheet.skipped');
  else if (st === 'locked') meta = t('sheet.locked', { rows: p.length });
  else meta = t('sheet.meta', { rows: p.length, level: t(`level.${p.level}`) });
  const cta =
    st === 'done'
      ? t('sheet.replay')
      : st === 'skipped'
        ? t('sheet.resume')
        : st === 'locked'
          ? t('sheet.soon')
          : t('sheet.play');
  const passes = passesLeft(content, ch);
  const canSkip = st === 'current' && passes > 0 && i < ch.puzzles.length - 1;
  return `<div class="sheet">
    <div class="sheet-txt"><b>${title}</b><span>${meta}</span></div>
    ${canSkip ? `<button class="btn btn-soft sheet-skip" type="button" data-act="skip">${t('sheet.skip')}<small>${t('sheet.passes', { n: passes, max: content.rules.passesPerChapter })}</small></button>` : ''}
    <button class="btn btn-go" type="button" data-act="play" ${st === 'locked' ? 'disabled' : ''}>${ICON.play}${cta}</button>
  </div>`;
}

function onClick(e: Event): void {
  const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
  if (!btn) return;
  const ch = content.chapters[view];
  switch (btn.dataset.act) {
    case 'prev':
      if (view > 0) renderHome(view - 1);
      break;
    case 'next':
      if (view < content.chapters.length - 1) renderHome(view + 1);
      break;
    case 'pick':
      sel = btn.dataset.id!;
      renderHome();
      break;
    case 'skip': {
      const cur = currentOf(ch);
      if (cur && passesLeft(content, ch) > 0) {
        skip(ch.id, cur.id);
        sel = currentOf(ch)?.id ?? sel;
        renderHome();
      }
      break;
    }
    case 'play': {
      const p = ch.puzzles.find((x) => x.id === sel);
      if (p && stateOf(content, view, p) !== 'locked') hooks.play(view, p);
      break;
    }
    case 'daily':
      hooks.daily();
      break;
    case 'invite-close':
      dismissInvite();
      renderHome();
      break;
    // Les Réglages (src/settings.ts) : le compte et le code de reprise y sont, avec le reste.
    case 'settings':
      hooks.settings();
      break;
  }
}
